// src/features/statistics/components/DemographicsFilterPanel.tsx

import { useMemo, useState } from 'react';
import { Check, ChevronDown, Filter } from 'lucide-react';

import { Button } from '../../../components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import { Skeleton } from '../../../components/ui/skeleton';
import { cn } from '../../../components/ui/utils';
import { getCountryName, sortedCountriesWithNames } from '../lib/country-names';
import { groupCitiesByCountry } from '../lib/city-names';
import { AGE_RANGES, countActiveFilters } from '../types/demographics-filter';

import type { DemographicsFilter } from '../types/demographics-filter';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pendingFilter: DemographicsFilter;
  onChange: (f: DemographicsFilter) => void;
  onApply: () => void;
  onReset: () => void;
  activeFilter: DemographicsFilter;
  availableCountries: string[];
  availableCities: string[];
  isFetchingDemographics: boolean;
  disabled?: boolean;
};

function CountryCombobox({
  values,
  onChange,
  countries,
}: {
  values: string[];
  onChange: (codes: string[]) => void;
  countries: string[];
}): JSX.Element {
  const [comboOpen, setComboOpen] = useState(false);
  const sorted = sortedCountriesWithNames(countries);

  return (
    <Popover open={comboOpen} onOpenChange={setComboOpen}>
      <PopoverTrigger asChild>
        <button
          role="combobox"
          aria-expanded={comboOpen}
          className={cn(
            'flex h-8 w-full items-center justify-between rounded-md border bg-background px-3 text-sm shadow-xs',
            'hover:bg-accent/40 focus:outline-none focus:ring-1 focus:ring-ring transition-colors',
            values.length === 0 && 'text-muted-foreground'
          )}
        >
          <span className="truncate">
            {values.length === 0 ? 'Страна' : `Страна (${values.length})`}
          </span>
          <ChevronDown className="size-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command>
          <CommandInput placeholder="Поиск страны..." />
          <CommandList className="max-h-52">
            <CommandEmpty>Страна не найдена</CommandEmpty>
            <CommandGroup>
              {sorted.map(({ code, name }) => (
                <CommandItem
                  key={code}
                  value={`${name} ${code}`}
                  onSelect={() => {
                    const next = values.includes(code)
                      ? values.filter(v => v !== code)
                      : [...values, code];
                    onChange(next);
                  }}
                >
                  <Check
                    className={cn('size-4', values.includes(code) ? 'opacity-100' : 'opacity-0')}
                  />
                  <span className="flex-1 truncate">{name}</span>
                  <span className="ml-2 text-xs text-muted-foreground">{code}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function CityCombobox({
  values,
  onChange,
  cities,
}: {
  values: string[];
  onChange: (cities: string[]) => void;
  cities: string[];
}): JSX.Element {
  const [comboOpen, setComboOpen] = useState(false);
  const groupedCities = useMemo(() => groupCitiesByCountry(cities), [cities]);

  return (
    <Popover open={comboOpen} onOpenChange={setComboOpen}>
      <PopoverTrigger asChild>
        <button
          role="combobox"
          aria-expanded={comboOpen}
          className={cn(
            'flex h-8 w-full items-center justify-between rounded-md border bg-background px-3 text-sm shadow-xs',
            'hover:bg-accent/40 focus:outline-none focus:ring-1 focus:ring-ring transition-colors',
            values.length === 0 && 'text-muted-foreground'
          )}
        >
          <span className="truncate">
            {values.length === 0 ? 'Город' : `Город (${values.length})`}
          </span>
          <ChevronDown className="size-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command>
          <CommandInput placeholder="Поиск города..." />
          <CommandList className="max-h-52">
            <CommandEmpty>Город не найден</CommandEmpty>
            {groupedCities.map(group => (
              <CommandGroup
                key={group.country}
                heading={group.country === '__other__' ? 'Другие' : getCountryName(group.country)}
              >
                {group.cities.map(city => (
                  <CommandItem
                    key={city.api}
                    value={`${city.nameRu} ${city.api}`}
                    onSelect={() => {
                      const next = values.includes(city.api)
                        ? values.filter(v => v !== city.api)
                        : [...values, city.api];
                      onChange(next);
                    }}
                  >
                    <Check
                      className={cn('size-4', values.includes(city.api) ? 'opacity-100' : 'opacity-0')}
                    />
                    <span className="flex-1 truncate">{city.nameRu}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function DemographicsFilterPanel({
  open,
  onOpenChange,
  pendingFilter,
  onChange,
  onApply,
  onReset,
  activeFilter,
  availableCountries,
  availableCities,
  isFetchingDemographics,
  disabled,
}: Props): JSX.Element {
  const activeCount = countActiveFilters(activeFilter);

  // When countries are selected, show only cities belonging to those countries
  const filteredAvailableCities = useMemo(() => {
    if (pendingFilter.countries.length === 0) return availableCities;
    const grouped = groupCitiesByCountry(availableCities);
    return grouped
      .filter(g => pendingFilter.countries.includes(g.country) || g.country === '__other__')
      .flatMap(g => g.cities.map(c => c.api));
  }, [availableCities, pendingFilter.countries]);

  return (
    <Popover open={disabled ? false : open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <span className="inline-block" title={disabled ? 'Демография недоступна для этого периода дат' : undefined}>
          <Button
            variant={activeCount > 0 ? 'default' : 'outline'}
            size="sm"
            className="gap-1.5 h-8"
            disabled={disabled}
          >
            <Filter className="size-3.5" />
            {activeCount > 0 ? `Фильтр: ${activeCount}` : 'Фильтр'}
          </Button>
        </span>
      </PopoverTrigger>

      <PopoverContent align="start" className="w-80 p-0">
        <div className="p-4 border-b">
          <p className="text-sm font-semibold">Демографический фильтр</p>
        </div>

        <div className="p-4 flex flex-col gap-4">
          {/* Geography */}
          <div className="flex flex-col gap-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              География
            </p>
            {isFetchingDemographics ? (
              <div className="flex flex-col gap-2">
                <Skeleton className="h-8 w-full rounded-md" />
                <Skeleton className="h-8 w-full rounded-md" />
              </div>
            ) : (
              <>
                <CountryCombobox
                  values={pendingFilter.countries}
                  onChange={codes => {
                    // Remove cities that no longer belong to any selected country
                    const grouped = groupCitiesByCountry(availableCities);
                    const allowed = new Set(
                      grouped
                        .filter(g => codes.includes(g.country) || g.country === '__other__')
                        .flatMap(g => g.cities.map(c => c.api))
                    );
                    const newCities = pendingFilter.cities.filter(c => allowed.has(c));
                    onChange({ ...pendingFilter, countries: codes, cities: newCities });
                  }}
                  countries={availableCountries}
                />

                <CityCombobox
                  values={pendingFilter.cities}
                  onChange={cities => onChange({ ...pendingFilter, cities })}
                  cities={filteredAvailableCities}
                />
              </>
            )}
          </div>

          {/* Gender */}
          <div className="flex flex-col gap-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Пол
            </p>
            <div className="flex gap-1.5">
              {(['all', 'F', 'M'] as const).map(g => (
                <Button
                  key={g}
                  size="sm"
                  variant={
                    (g === 'all' && !pendingFilter.gender) || pendingFilter.gender === g
                      ? 'default'
                      : 'outline'
                  }
                  className="h-7 text-xs flex-1"
                  onClick={() =>
                    onChange({ ...pendingFilter, gender: g === 'all' ? null : g })
                  }
                >
                  {g === 'all' ? 'Все' : g === 'F' ? 'Женщины' : 'Мужчины'}
                </Button>
              ))}
            </div>
          </div>

          {/* Age */}
          <div className="flex flex-col gap-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Возраст
            </p>
            <div className="flex flex-wrap gap-1.5">
              <Button
                size="sm"
                variant={pendingFilter.ageRanges.length === 0 ? 'default' : 'outline'}
                className="h-7 text-xs"
                onClick={() => onChange({ ...pendingFilter, ageRanges: [] })}
              >
                Все
              </Button>
              {AGE_RANGES.map(range => (
                <Button
                  key={range}
                  size="sm"
                  variant={pendingFilter.ageRanges.includes(range) ? 'default' : 'outline'}
                  className="h-7 text-xs"
                  onClick={() => {
                    const next = pendingFilter.ageRanges.includes(range)
                      ? pendingFilter.ageRanges.filter(r => r !== range)
                      : [...pendingFilter.ageRanges, range];
                    onChange({ ...pendingFilter, ageRanges: next });
                  }}
                >
                  {range}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 border-t flex justify-between gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onReset();
              onOpenChange(false);
            }}
          >
            Сбросить
          </Button>
          <Button
            size="sm"
            disabled={isFetchingDemographics}
            onClick={() => {
              onApply();
              onOpenChange(false);
            }}
          >
            {isFetchingDemographics ? 'Загрузка...' : 'Применить'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
